import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';
import { getLineItemModifications } from '@sg/shared-fe-features-ordering';

import {
  type PartialLineItem,
  type UpdateLineItemParams,
  type UpdateLineItemResponse,
} from '@order/features-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  RemoveLineItemDocument,
  type RemoveLineItemMutation,
  type RemoveLineItemMutationVariables,
  UpdateLineItemDocument,
  type UpdateLineItemMutation,
  type UpdateLineItemMutationVariables,
} from '../../GraphQL/BagCart.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for updating the line item in the cart.
 * If a {quantity} of 0 is passed, it will call the remove mutation.
 * Otherwise it will call the update mutation for either quantity or custom name.
 */
export const useUpdateLineItem = () => {
  const client = useClient();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  const isSelectedModifiersEnabled = useFeatureFlag(
    'CELS-5151-use-selected-modifiers-to-edit-line-item-enabled',
  );

  // ─── Mutation ─────────────────────────────────────────────────────────────

  const updateLineItemMutation = useCallback(
    async (lineItem: PartialLineItem, params: UpdateLineItemParams) => {
      const updateMutation = client.mutation<
        UpdateLineItemMutation,
        UpdateLineItemMutationVariables
      >;

      const removeMutation = client.mutation<
        RemoveLineItemMutation,
        RemoveLineItemMutationVariables
      >;

      if (params.quantity === 0) {
        const response = await removeMutation(
          RemoveLineItemDocument,
          {
            input: {
              lineItemId: params.lineItemId,
            },
          },
          {
            requestPolicy: 'network-only',
          },
        ).toPromise();

        return response.data?.removeFromCart;
      }

      const legacyModifications = {
        additions: lineItem.addedIngredients?.map(getIngredientId) ?? [],
        removals: lineItem.removedIngredients?.map(getIngredientId) ?? [],
        substitutions: [],
      };
      const updatedModifications = getLineItemModifications({
        selectedModifiers: lineItem.selectedModifiers ?? [],
        isCustomProduct: lineItem.product?.isCustom ?? false,
      });
      const shouldUseUpdatedModifications =
        isSelectedModifiersEnabled && lineItem.selectedModifiers;

      const modifications = shouldUseUpdatedModifications
        ? updatedModifications
        : legacyModifications;

      const response = await updateMutation(
        UpdateLineItemDocument,
        {
          input: {
            lineItemId: params.lineItemId,
            productId: params.productId,
            quantity: params.quantity,
            customName: lineItem.customName,
            mixedDressingDetails: lineItem.mixedDressingDetails ?? [],
            ...modifications,
          },
        },
        {
          requestPolicy: 'network-only',
        },
      ).toPromise();

      return response.data?.editLineItemInCart;
    },
    [client.mutation, isSelectedModifiersEnabled],
  );
  // ─── Helpers ──────────────────────────────────────────────────────────────

  const updateLineItem = useMemo(
    () =>
      fromPromise<UpdateLineItemResponse, UpdateLineItemParams>(async ({ input }) => {
        const { lineItemId, lineItems } = input;
        const lineItem = lineItems.find(({ id }) => id === lineItemId);

        if (!lineItem) return;

        const defaultError = formatMessage(messages.defaultError);

        const response = await updateLineItemMutation(lineItem, input);

        if (
          response?.__typename === 'EditLineItemInCartSuccess' ||
          response?.__typename === 'RemoveFromCartSuccess'
        ) {
          return prepareCart(response.cart as Partial<Order>);
        }

        if (!response) {
          addNoticeBanner({ text: defaultError, palette: 'caution' });
          input.resetQuantity?.();
          return;
        }

        if (response.__typename === 'ValidationError') {
          addNoticeBanner({
            text: response?.message ?? response?.fieldErrors?.[0].message,
            palette: 'caution',
          });
          input.resetQuantity?.();
          return;
        }

        if ('message' in response) {
          addNoticeBanner({ text: response.message, palette: 'caution' });
          input.resetQuantity?.();
          return;
        }

        addNoticeBanner({ text: defaultError, palette: 'caution' });
        input.resetQuantity?.();
      }),
    [addNoticeBanner, formatMessage, updateLineItemMutation],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { updateLineItem };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getIngredientId = (ingredient?: { id?: string }) => {
  return { ingredientId: ingredient?.id ?? '' };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  defaultError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Bag | Update Line Item | Default Error',
  },
});
